import { useMemo, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table, TableProps } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { toTitleCase } from 'Helpers/StringUtils';
import { getCreateExceptionUrl, getExceptionDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { ExceptionHistoryModificationsResponse, ExceptionResponse, ExceptionRiskScore, ExceptionStatus, ManageExceptionOrigin, riskScoreComparator, statusComparator } from 'Models/Exceptions';
import { OperationalControl } from 'Models/OperationalControls';
import { OrganizationArchiveBase, OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ColorTheme } from 'Models/Types/GlobalType';
import { ExceptionHistoryModal } from 'Pages/IssuesExceptions/Exceptions/Components/ExceptionHistoryModal/ExceptionHistoryModal';

import styles from './ExceptionsListing.module.css';

/**
 * @param colorTheme    - The type of background that the component will be displayed against. If not supplied, `'light'` will be used.
 * @param hideTitle     - If `true`, the title will not be displayed. This is useful since sometimes this component is used in a tab of the same name, making the title redundant. In other cases, Issues and Exceptions are shown together, so having a title for each is necessary to differentiate them.
 */
export interface ExceptionsListingBaseProps {
    createButtonVariant: 'primaryButton' | 'secondaryButton';
    createExceptionOrigin: ManageExceptionOrigin; // Supplies the context for an origin-aware breadcrumb on the Manage Exception page reached via the "Create Exception" button.

    colorTheme?: ColorTheme;
    hideTitle?: boolean;
    hideCreateButton?: boolean;
    useHorizontalRuleBelowTitle?: boolean; // This could be cleaner, but it's the fastest way to remain backwards-compatible for now (since our theming is inconsistent at worst and difficult to get right in child components at best, IMO).
}

export interface ExceptionsListingControlsProps extends ExceptionsListingBaseProps {
    type: ProductModule.CONTROLS;
    exceptions: ExceptionResponse[];
    preselectedControlIdForCreate?: string;
    displayMappedControlsModal: (mappedControls: OperationalControl[]) => void;
    organizations?: never; // Typed like this so that `organizations` can be used unconditionally as a `useMemo` dependency.
}

export interface ExceptionsListingDueDiligenceProps extends ExceptionsListingBaseProps {
    type: DueDiligenceModule;
    exceptions: ExceptionResponse[] | ExceptionHistoryModificationsResponse[];
    organizations: (OrganizationResponseBase | OrganizationArchiveBase)[];
    documentApi: DocumentApi;
    preselectedOrganizationIdForCreate?: string;
    preselectedOrganizationIdForFilter?: string;
    disableOrganizationFilter?: boolean;
    hideOrganizationInfo?: boolean; // TODO: Implement this more cleanly. Possibly remove the above prop.
    /** When `true`, `exceptions` are `ExceptionHistoryModificationsResponse`, whose `tags` are already-resolved `DisplayableTag`s rather than tag IDs. This drives whether the Tags column looks up current tag metadata or renders the historical tags directly. */
    historical?: boolean;
}

export type ExceptionsListingProps = ExceptionsListingControlsProps | ExceptionsListingDueDiligenceProps;

/**
 * Renders a table of exceptions, which are either Operational Controls exceptions or due diligence (TPRM or UCRM) exceptions.
 * The `type` prop determines which type of exceptions are displayed. This affects some minor things, such as whether a "Controls" column or an organization ("Third Party" or "Business") column is rendered.
 *
 * In addition to the table, this component renders:
 * * A header section with the text "Exceptions" and a "Create Exception" button.
 * * Filters for owner, reviewer, risk score, status, and organization (if TPRM or UCRM exceptions are being listed).
 */
export const ExceptionsListing = (props: ExceptionsListingProps) => {
    const cachedData = useCachedData();

    // Historical exceptions may have since been deleted, so instead of linking to a (possibly missing) details page, clicking one opens a read-only snapshot modal. Only ever set in TPRM historical mode.
    const [selectedHistoricalException, setSelectedHistoricalException] = useState<ExceptionHistoryModificationsResponse>();

    const organizationIdToNameMap: Map<string, string> | undefined = useMemo(() => {
        return props.type !== ProductModule.CONTROLS ? new Map(props.organizations.map((organization) => [organization.id, organization.name])) : undefined;
    }, [props.type, props.organizations]);

    const getOrganizationName = organizationIdToNameMap ? (organizationId: string) => organizationIdToNameMap.get(organizationId)! : undefined;

    const columns: ColumnConfig<ExceptionResponse | ExceptionHistoryModificationsResponse>[] = [
        {
            type: ColumnType.STRING,
            name: 'Title',
            getString: (exception) => exception.title,
            getOnClickTextAction: (exception) => (props.type !== ProductModule.CONTROLS && props.historical ? { execute: (row) => setSelectedHistoricalException(row as ExceptionHistoryModificationsResponse) } : { goToLocation: getExceptionDetailsUrl(exception.id, exception.type) }),
        },
        {
            type: ColumnType.DATE,
            name: 'Expiration',
            getDateString: (exception) => exception.expiration_date,
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (exception) => exception.owner_subject,
            users: cachedData.users,
        },
        {
            type: ColumnType.USER,
            name: 'Reviewer',
            getUserId: (exception) => exception.reviewer_subject,
            users: cachedData.users,
        },
        {
            type: ColumnType.ENUM,
            name: 'Risk Score',
            getString: (exception) => toTitleCase(exception.risk_score, '/'),
            sortComparator: (a, b) => riskScoreComparator(a.risk_score!, b.risk_score!),
        },
        ...(props.type === ProductModule.CONTROLS
            ? [
                  {
                      type: ColumnType.MULTIPLE_CONTROLS as const,
                      name: 'Controls',
                      getControls: (exception: ExceptionResponse | ExceptionHistoryModificationsResponse) => (exception as ExceptionResponse).impacted_controls,
                      onClick: props.displayMappedControlsModal,
                  },
              ]
            : []),
        ...(props.type !== ProductModule.CONTROLS && !props.hideOrganizationInfo
            ? [
                  {
                      type: ColumnType.STRING as const,
                      name: props.type === ProductModule.UCRM ? 'Business' : 'Third Party',
                      getString: (exception: ExceptionResponse | ExceptionHistoryModificationsResponse) => getOrganizationName!(exception.impacted_vendor!),
                  },
              ]
            : []),
        {
            type: ColumnType.ENUM,
            name: 'Status',
            getString: (exception) => toTitleCase(exception.status),
            sortComparator: (a, b) => statusComparator(a.status, b.status),
        },
        ...(props.type !== ProductModule.CONTROLS && props.historical
            ? [
                  {
                      type: ColumnType.MULTIPLE_HISTORICAL_TAGS as const,
                      name: 'Tags',
                      getTags: (exception: ExceptionResponse | ExceptionHistoryModificationsResponse) => (exception as ExceptionHistoryModificationsResponse).tags,
                  },
              ]
            : [
                  {
                      type: ColumnType.MULTIPLE_TAGS as const,
                      name: 'Tags',
                      getTagIds: (exception: ExceptionResponse | ExceptionHistoryModificationsResponse) => (exception as ExceptionResponse).tags,
                      convertTagIdsToDisplayableTags: cachedData.convertTagIdsToDisplayableTags,
                  },
              ]),
    ];

    const filters: FilterConfig<ExceptionResponse | ExceptionHistoryModificationsResponse>[] = [
        ...(props.type !== ProductModule.CONTROLS && !props.hideOrganizationInfo
            ? [
                  {
                      type: FilterType.SINGLE_PROPERTY as const,
                      id: 'organizationFilter',
                      label: props.type === ProductModule.UCRM ? 'Filter by Business' : 'Filter by Third Party',
                      options: props.organizations.map((organization) => ({ name: organization.name, value: organization.id })),
                      getFilterableDataForRow: (exception: ExceptionResponse | ExceptionHistoryModificationsResponse) => (exception.impacted_vendor ? [exception.impacted_vendor] : []),
                      canSelectMultiple: true,
                      defaultSelections: props.preselectedOrganizationIdForFilter ? [props.preselectedOrganizationIdForFilter] : undefined,
                      disabled: props.disableOrganizationFilter,
                  },
              ]
            : []),
        {
            type: FilterType.MULTIPLE_PROPERTIES,
            id: 'riskOrStatusFilter',
            label: 'Filter by Risk Score or Status',
            groups: [
                {
                    label: 'Risk Score',
                    options: [
                        { name: 'Low', value: ExceptionRiskScore.LOW },
                        { name: 'Low/Moderate', value: ExceptionRiskScore.LOW_MODERATE },
                        { name: 'Moderate', value: ExceptionRiskScore.MODERATE },
                        { name: 'Moderate/High', value: ExceptionRiskScore.MODERATE_HIGH },
                        { name: 'High', value: ExceptionRiskScore.HIGH },
                    ],
                    getFilterableDataForRow: (exception) => (exception.risk_score ? [exception.risk_score] : []),
                },
                {
                    label: 'Status',
                    options: [
                        { name: 'Draft Open', value: ExceptionStatus.DRAFT_OPEN },
                        { name: 'Approved', value: ExceptionStatus.APPROVED },
                        { name: 'Draft Close', value: ExceptionStatus.DRAFT_CLOSE },
                        { name: 'Closed', value: ExceptionStatus.CLOSED },
                    ],
                    getFilterableDataForRow: (exception) => [exception.status],
                    defaultSelections: [ExceptionStatus.DRAFT_OPEN, ExceptionStatus.APPROVED, ExceptionStatus.DRAFT_CLOSE],
                },
            ],
        },
        {
            type: FilterType.USERS,
            id: 'usersFilter',
            label: 'Filter by Owner',
            getUserIdsForRow: (exception) => (exception.owner_subject ? [exception.owner_subject] : []),
        },
        {
            type: FilterType.TAGS,
            id: 'tagsFilter',
            label: 'Filter by Tags',
            getTagIdsForRow: (exception) => (props.type !== ProductModule.CONTROLS && props.historical ? (exception as ExceptionHistoryModificationsResponse).tags.map((tag) => tag.id) : ((exception as ExceptionResponse).tags ?? [])),
        },
    ];

    const tableProps: TableProps<ExceptionResponse | ExceptionHistoryModificationsResponse> = {
        columns,
        filters,
        defaultSortColumn: 'Status',
        rowsData: props.exceptions,
        getUniqueIdForRow: (exception) => exception.id,
        pageVariant: (props.colorTheme ?? 'light') === 'light' ? 'standard' : 'dashboard',
    };

    const createButtonDestination: string = (() => {
        if (props.type === ProductModule.CONTROLS) {
            return getCreateExceptionUrl(ProductModule.CONTROLS, props.createExceptionOrigin, props.preselectedControlIdForCreate);
        } else {
            return getCreateExceptionUrl(props.type, props.createExceptionOrigin, props.preselectedOrganizationIdForCreate);
        }
    })();

    return (
        <>
            {!props.hideTitle ? (
                <>
                    <div className={styles.titleAndButtonContainer}>
                        <Text variant="Header2" color={props.colorTheme === 'dark' ? 'white' : 'blue'}>
                            Exceptions
                        </Text>
                        {!props.hideCreateButton && (
                            <Link variant={props.createButtonVariant} to={createButtonDestination} fontAwesomeImage={ICON_ADD_CREATE}>
                                Create Exception
                            </Link>
                        )}
                    </div>
                    {props.useHorizontalRuleBelowTitle && (
                        <div className={styles.horizontalRule}>
                            <hr />
                        </div>
                    )}
                </>
            ) : (
                <>
                    {!props.hideCreateButton && (
                        <div className={styles.alignRight}>
                            <Link variant={props.createButtonVariant} to={createButtonDestination} fontAwesomeImage={ICON_ADD_CREATE}>
                                Create Exception
                            </Link>
                        </div>
                    )}
                </>
            )}
            <Table {...tableProps} />
            {selectedHistoricalException && props.type !== ProductModule.CONTROLS && <ExceptionHistoryModal history={{ timestamp: selectedHistoricalException.last_updated_timestamp, exception: selectedHistoricalException }} documentApi={props.documentApi} hideModal={() => setSelectedHistoricalException(undefined)} />}
        </>
    );
};
